import type { PropsWithChildren, ReactNode } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import { GovIcon } from '@gov-design-system-ce/react';

export interface TileButtonProps {
	onClick: () => void;
}

export interface TileLinkProps {
	to: string;
}

export interface TileNotClickableProps {}

interface CommonProps {
	title: string;
	children?: ReactNode;
	icon?: ReactNode;
	favoriteToggle?: ReactNode;
}

type Props = CommonProps &
	(TileLinkProps | TileButtonProps | TileNotClickableProps);

const isLinkProps = (props: Props): props is TileLinkProps & CommonProps =>
	(props as TileLinkProps).to !== undefined;
const isButtonProps = (props: Props): props is TileButtonProps & CommonProps =>
	(props as TileButtonProps).onClick !== undefined;

const ContainerComponent = (props: PropsWithChildren<Props>) => {
	if (isLinkProps(props)) {
		return (
			<Link
				to={props.to}
				className="anim-bg-hover flex gap-(--templates-margin-l) justify-between items-start w-full pt-(--templates-margin-l) pb-(--templates-margin-l) pr-(--templates-margin-m) pl-(--templates-margin-l) no-underline! relative">
				{props.children}
			</Link>
		);
	}

	if (isButtonProps(props)) {
		return (
			<button
				onClick={props.onClick}
				className="anim-bg-hover flex gap-(--templates-margin-l) justify-between items-start w-full pt-(--templates-margin-l) pb-(--templates-margin-l) pr-(--templates-margin-m) pl-(--templates-margin-l) no-underline! cursor-pointer relative">
				{props.children}
			</button>
		);
	}

	return <div>{props.children}</div>;
};

export const Tile = (props: Props) => {
	const { title, children, icon, favoriteToggle } = props;

	const isClickable = isLinkProps(props) || isButtonProps(props);

	return (
		<li className="border-b border-(--border-subtlest) [ last-of-type:border-b-0 ]">
			<ContainerComponent {...props}>
				{icon ? <div className="grow-0 shrink-0 basis-10">{icon}</div> : null}
				<div className="grow gap-s flex flex-col justify-start text-left">
					<h3 className="text-headline-s! text-(--text-primary-color)">
						{title}
					</h3>
					{children}
				</div>
				{isClickable ? (
					<GovIcon
						name="chevron-right"
						type="components"
						size="m"
						color="primary"
						slot="icon"
						className="relative top-2"
					/>
				) : null}
			</ContainerComponent>

			{favoriteToggle ? (
				<div className="absolute top-14 right-1 z-30">{favoriteToggle}</div>
			) : null}
		</li>
	);
};

export const Tiles = ({ children }: PropsWithChildren) => (
	<ul className="gov-list--plain">{children}</ul>
);
